import { PropsWithChildren, ReactNode } from "react";

import { css } from "@compiled/react";

import {
    ConfluenceLogo,
    JiraLogo,
    LoomLogo,
    JiraServiceManagementLogo
} from "@atlaskit/logo";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import {
    ConfluenceBackground,
    JiraBackground,
    LoomBackground,
    JiraProductDiscoveryBackground
} from "@atlassian/product-wireframes";

import { Picture } from "@/components/wpl/image/src";
import { ClientOnly } from "@/utils/client-only";

import { Modal } from "./modal";

export enum LayoutVersion {
    V1 = "V1",
    V2 = "V2",
    V3 = "V3"
}

type ProductOverlayTemplateProps = PropsWithChildren<{
    clientOnly?: boolean;
    header?: string;
    subheader?: string;
    size?: "sm" | "lg";
    productLogo: ReactNode;
    productBackground: React.ComponentType<{ children: ReactNode }>;
    showBlueHeaderOnMobile?: boolean;
    showBrandFooter?: boolean;
    fadeOutContent?: boolean;
    version?: LayoutVersion;
}>;

const ProductOverlayTemplate = ({
    children,
    clientOnly = true,
    header,
    subheader,
    size,
    productLogo,
    productBackground,
    showBlueHeaderOnMobile = false,
    showBrandFooter = true,
    fadeOutContent = false,
    version = LayoutVersion.V1
}: ProductOverlayTemplateProps) => {
    const ProductBackground = productBackground;

    const template = (
        <ProductBackground>
            <Modal
                showBlueHeaderOnMobile={showBlueHeaderOnMobile}
                size={size}
                showBrandFooter={showBrandFooter}
                version={version}
            >
                {version === LayoutVersion.V2 ? (
                    <div css={LayoutV2.innerModalCss}>{children}</div>
                ) : version === LayoutVersion.V3 ? (
                    <div css={LayoutV3.innerModalCss}>{children}</div>
                ) : (
                    <div css={[innerModalCss]}>
                        <div
                            css={[
                                productLogoContainerCss,
                                fadeOutContent && fade
                            ]}
                        >
                            {productLogo}
                        </div>
                        {header && (
                            <h1 css={[headerCss, fadeOutContent && fade]}>
                                {header}
                            </h1>
                        )}
                        {subheader && (
                            <p css={[subheaderCss, fadeOutContent && fade]}>
                                {subheader}
                            </p>
                        )}
                        {children}
                    </div>
                )}
            </Modal>
        </ProductBackground>
    );

    if (clientOnly) {
        return <ClientOnly>{template}</ClientOnly>;
    }

    return template;
};

export const ConfluenceOverlayTemplate = ({
    children,
    header,
    subheader,
    size
}: PropsWithChildren<{
    header?: string;
    subheader?: string;
    size?: "sm" | "lg";
}>) => (
    <ProductOverlayTemplate
        productLogo={<ConfluenceLogo appearance="brand" size="medium" />}
        productBackground={ConfluenceBackground}
        header={header}
        subheader={subheader}
        size={size}
        showBlueHeaderOnMobile={true}
        clientOnly={false}
    >
        {children}
    </ProductOverlayTemplate>
);

// Backward compatibility: Keep the JiraOverlayTemplate as a wrapper
export const JiraOverlayTemplate = ({
    children,
    header,
    subheader,
    size,
    fadeOutContent,
    version
}: PropsWithChildren<{
    header?: string;
    subheader?: string;
    size?: "sm" | "lg";
    fadeOutContent?: boolean;
    version?: LayoutVersion;
}>) => (
    <ProductOverlayTemplate
        productLogo={
            version === LayoutVersion.V2 ||
            version === LayoutVersion.V3 ? undefined : (
                <JiraLogo size="small" appearance="brand" />
            )
        }
        productBackground={JiraBackground}
        header={header}
        subheader={subheader}
        size={size}
        clientOnly={false}
        fadeOutContent={fadeOutContent}
        version={version}
        showBrandFooter={
            version === LayoutVersion.V2 || version === LayoutVersion.V3
                ? false
                : true
        }
    >
        {children}
    </ProductOverlayTemplate>
);

// Loom overlay template using the parameterized component
export const LoomOverlayTemplate = ({
    children,
    header,
    subheader,
    size
}: PropsWithChildren<{
    header?: string;
    subheader?: string;
    size?: "sm" | "lg";
}>) => (
    <ProductOverlayTemplate
        productLogo={<LoomLogo size="small" appearance="brand" />}
        productBackground={LoomBackground}
        header={header}
        subheader={subheader}
        size={size}
    >
        {children}
    </ProductOverlayTemplate>
);

// JSM overlay template using the parameterized component
export const JSMOverlayTemplate = ({
    children,
    header,
    subheader,
    size
}: PropsWithChildren<{
    header?: string;
    subheader?: string;
    size?: "sm" | "lg";
}>) => (
    <ProductOverlayTemplate
        productLogo={
            <JiraServiceManagementLogo size="small" appearance="brand" />
        }
        // using Jira Background to match @atlassiansox/cross-flow-support until updated there
        productBackground={JiraBackground}
        header={header}
        subheader={subheader}
        size={size}
    >
        {children}
    </ProductOverlayTemplate>
);

// JPD overlay template using the parameterized component
export const JPDOverlayTemplate = ({
    children,
    header,
    subheader,
    size,
    showBrandFooter = true
}: PropsWithChildren<{
    header?: string;
    subheader?: string;
    size?: "sm" | "lg";
    showBrandFooter?: boolean;
}>) => (
    // TODO: once refreshed logos are made avaliable in WAC, update this to use the new @atlaskit/logo
    <ProductOverlayTemplate
        productLogo={
            <Picture
                image={{
                    src: "https://dam-cdn.atl.orangelogic.com/AssetLink/108wt817r553ywiinjjke21wmaoi4l2a.webp",
                    alt: "Jira Product Discovery",
                    width: 246,
                    height: 24
                }}
            />
        }
        productBackground={JiraProductDiscoveryBackground}
        header={header}
        subheader={subheader}
        size={size}
        showBrandFooter={showBrandFooter}
    >
        {children}
    </ProductOverlayTemplate>
);

const productLogoContainerCss = css({
    display: "flex",
    justifyContent: "center",
    marginBlockStart: "8px",
    marginBlockEnd: "16px"
});

const headerCss = css({
    fontSize: 32,
    fontWeight: 700,
    lineHeight: 1.2,
    margin: "24px 0 0",
    textAlign: "center"
});

const subheaderCss = css({
    margin: "8px 0 24px",
    textAlign: "center"
});

const fade = css({
    opacity: 0,
    transition: "opacity 0.5s ease-in-out"
});

const innerModalCss = css({
    alignItems: "stretch",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    [media.above.sm]: {
        padding: "40px 80px 80px"
    }
});

const LayoutV2 = {
    innerModalCss: css({
        position: "relative",
        alignItems: "stretch",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: 640,
        [media.above.sm]: {
            padding: token("space.800", "64px")
        },
        overflow: "hidden"
    })
};

const LayoutV3 = {
    innerModalCss: css({
        position: "relative",
        alignItems: "stretch",
        color: token("color.text.subtle"),
        display: "flex",
        flexDirection: "column",
        fontFamily: `'Atlassian Sans', ui-sans-serif, -apple-system, 'system-ui', 'Segoe UI', Ubuntu, 'Helvetica Neue', sans-serif`, // todo replace with token when available
        margin: "0 auto",
        maxWidth: 432,
        width: "100%",
        minHeight: 560,
        [media.above.sm]: {
            maxWidth: 560,
            padding: token("space.800", "64px")
        },
        overflow: "hidden"
    })
};
